import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { User, Mail, Lock, Sparkles, ArrowRight, ShieldCheck } from "lucide-react";

import serumImg from "../assests/faceimg.webp"; // Path check karein

const Auth = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");

    const endpoint = isLogin ? "/api/auth/login" : "/api/auth/signup";

    try {
      const res = await fetch(`http://localhost:1010${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (res.ok) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user));
        navigate("/profile");
      } else {
        setErrorMsg(data.message || "Something went wrong");
      }
    } catch (err) {
      setErrorMsg("Backend server is not reachable on port 1010");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] bg-[#FAF8F5] py-12 px-4 flex items-center justify-center">
      <div className="w-full max-w-4xl bg-white rounded-3xl shadow-2xl border border-[#E8E2D9] overflow-hidden grid grid-cols-1 md:grid-cols-12 min-h-[580px]">
        
        {/* Left Editorial Visual Section */}
        <div className="md:col-span-5 relative hidden md:block bg-[#1F1B18] overflow-hidden">
          <img
            src={serumImg}
            alt="Celiora Luxury Skincare"
            className="absolute inset-0 w-full h-full object-cover opacity-85"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
          <div className="relative z-10 h-full p-8 flex flex-col justify-between text-white">
            <Link to="/" className="text-xs uppercase tracking-[3px] font-semibold text-[#9D7D4F] flex items-center gap-1">
              <Sparkles size={14} /> CELIORA CLUB
            </Link>

            <div>
              <h2 className="font-serif text-2xl tracking-wide leading-tight mb-3">
                {isLogin ? "Welcome Back to Your Daily Glow" : "Join Our Seoul Beauty Club"}
              </h2>
              <p className="text-xs text-gray-300 font-light leading-relaxed">
                Unlock 20% off your next order, custom skincare routine recommendations, and VIP drops.
              </p>
            </div>

            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-gray-400">
              <ShieldCheck size={14} className="text-[#9D7D4F]" /> Dermatologist Formulated
            </div>
          </div>
        </div>

        {/* Right Form Container */}
        <div className="md:col-span-7 p-8 sm:p-12 flex flex-col justify-center">
          
          {/* Tab Selection */}
          <div className="flex border-b border-[#E8E2D9] mb-8">
            <button
              onClick={() => { setIsLogin(true); setErrorMsg(""); }}
              className={`pb-3 text-xs tracking-[2px] uppercase font-semibold transition-all relative ${
                isLogin ? "text-[#1F1B18]" : "text-gray-400 hover:text-black"
              }`}
            >
              Sign In
              {isLogin && <span className="absolute bottom-0 left-0 w-full h-[2px] bg-[#9D7D4F]" />}
            </button>
            <button
              onClick={() => { setIsLogin(false); setErrorMsg(""); }}
              className={`pb-3 ml-8 text-xs tracking-[2px] uppercase font-semibold transition-all relative ${
                !isLogin ? "text-[#1F1B18]" : "text-gray-400 hover:text-black"
              }`}
            >
              Create Account
              {!isLogin && <span className="absolute bottom-0 left-0 w-full h-[2px] bg-[#9D7D4F]" />}
            </button>
          </div>

          {errorMsg && (
            <div className="mb-5 p-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-xs">
              {errorMsg}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-[#7B736C] font-semibold mb-1">
                  Full Name
                </label>
                <div className="relative">
                  <input
                    type="text"
                    required
                    placeholder="Aanya Sharma"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-[#FAF8F5] border border-[#E8E2D9] rounded-xl py-3 pl-10 pr-4 text-xs focus:outline-none focus:border-[#9D7D4F] text-[#1F1B18]"
                  />
                  <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
              </div>
            )}

            <div>
              <label className="block text-[10px] uppercase tracking-wider text-[#7B736C] font-semibold mb-1">
                Email Address
              </label>
              <div className="relative">
                <input
                  type="email"
                  required
                  placeholder="name@domain.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full bg-[#FAF8F5] border border-[#E8E2D9] rounded-xl py-3 pl-10 pr-4 text-xs focus:outline-none focus:border-[#9D7D4F] text-[#1F1B18]"
                />
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-wider text-[#7B736C] font-semibold mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type="password"
                  required
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full bg-[#FAF8F5] border border-[#E8E2D9] rounded-xl py-3 pl-10 pr-4 text-xs focus:outline-none focus:border-[#9D7D4F] text-[#1F1B18]"
                />
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#1F1B18] text-white py-3.5 rounded-xl text-xs uppercase tracking-[2px] font-semibold hover:bg-[#9D7D4F] transition flex items-center justify-center gap-2 mt-2 shadow-md"
            >
              <span>{loading ? "Please wait..." : isLogin ? "Sign In" : "Register Account"}</span>
              <ArrowRight size={14} />
            </button>
          </form>

        </div>
      </div>
    </div>
  );
};

export default Auth;